import { Calendar } from "primereact/calendar";
import { formatTime } from "utils/dateFormat";
// Registers the Arabic locale of the picker (the «الآن» and «مسح» buttons).
import "components/Ui/DateTimeField/DateTimeField";
import "./timeField.scss";

const pad = (number) => String(number).padStart(2, "0");

/**
 * "HH:mm" or "HH:mm:ss" → a Date on a fixed day, for the picker.
 *
 * @param {string|null|undefined} value
 * @returns {Date|null}
 */
export const timeToDate = (value) => {
    const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(String(value ?? "").trim());
    if (!match) return null;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (hour > 23 || minute > 59) return null;
    return new Date(2000, 0, 1, hour, minute);
};

/**
 * The picker's Date → "HH:mm", the shape the forms keep and the API takes.
 *
 * @param {Date|null|undefined} date
 * @returns {string} "" when there is no time
 */
export const dateToTime = (date) =>
    date instanceof Date && !Number.isNaN(date.getTime())
        ? `${pad(date.getHours())}:${pad(date.getMinutes())}`
        : "";

// Where the clock of an EMPTY field opens: 12:00 PM. Without it the picker
// opens on the current time, such as 10:57 PM, and the operator must step the
// minutes back to :00 before he can choose an hour.
const EMPTY_FIELD_VIEW = new Date(2000, 0, 1, 12, 0);

/**
 * A time of day. EVERY TIME INPUT IN THE DASHBOARD IS ONE OF THESE.
 *
 * NOT `<input type="time">`. That control follows the browser: one operator
 * saw «10:00 AM» and another saw «22:00» for the same form. This field always
 * reads «10:00 AM», the dashboard's one time format (utils/dateFormat.js).
 *
 * THE VALUE DOES NOT CHANGE SHAPE. It goes in and comes out as the 24-hour
 * string "HH:mm", the same as the native input gave. Only the text on screen
 * is 12-hour. So a form that used the native input sends what it sent before.
 *
 * @param {object} props
 * @param {string} props.value - "HH:mm" ("HH:mm:ss" is read too), or "" when unset
 * @param {(value: string) => void} props.onChange - receives "HH:mm", or ""
 * @param {boolean} [props.disabled]
 * @param {string} [props.inputId] - for a label's htmlFor
 * @param {string} [props.placeholder]
 * @param {string} [props.className]
 */
const TimeField = ({
    value,
    onChange,
    disabled = false,
    inputId,
    placeholder = "--:--",
    className = "",
    ...rest
}) => {
    const parsed = timeToDate(value);

    return (
        <Calendar
            inputId={inputId}
            value={parsed}
            {...(parsed ? {} : { viewDate: EMPTY_FIELD_VIEW })}
            onChange={(event) => onChange(dateToTime(event.value))}
            timeOnly
            hourFormat="12"
            formatDateTime={formatTime}
            disabled={disabled}
            showButtonBar
            readOnlyInput
            placeholder={placeholder}
            className={`time-field ${className}`.trim()}
            // At the body: the forms that use it are modals, which clip an inline
            // panel. The z-index above the modals is on `.date-filter__panel`.
            appendTo={typeof document === "undefined" ? undefined : document.body}
            panelClassName="date-filter__panel time-field__panel"
            {...rest}
        />
    );
};

export default TimeField;
